import { useEffect, useRef } from "react";
import "@/App.css";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "@studio-freight/lenis";
import { MessageSquare, Phone, Send } from "lucide-react";

function App() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const logoOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const logoScale = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <div className="relative bg-black text-white overflow-hidden">
      <div className="grain-overlay" />

      {/* Navbar */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-black/50 border-b border-white/10"
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <img
            src="https://customer-assets.emergentagent.com/job_dc130d97-6b55-4e93-ba73-e94270a0cce0/artifacts/npzerimb_IMG_1769.png"
            alt="Infinity Influencers"
            className="h-12 md:h-14"
            data-testid="navbar-logo"
          />
          <div className="flex gap-6">
            <a href="#sobre" className="text-zinc-400 hover:text-gold transition-colors" data-testid="nav-link-sobre">
              Sobre
            </a>
            <a href="#porque" className="text-zinc-400 hover:text-gold transition-colors" data-testid="nav-link-porque">
              Diferenciais
            </a>
            <a href="#contato" className="text-zinc-400 hover:text-gold transition-colors" data-testid="nav-link-contato">
              Contato
            </a>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section ref={heroRef} className="relative h-screen flex flex-col items-center justify-start pt-24" data-testid="hero-section">
        <motion.div 
          style={{ opacity: logoOpacity, scale: logoScale }} 
          className="parallax-logo flex-shrink-0 mb-8"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_dc130d97-6b55-4e93-ba73-e94270a0cce0/artifacts/774mz0cl_75030850-B02B-4672-9972-7894C223C4EE.png"
            alt="Infinity Influencers Logo"
            className="w-full max-w-2xl mx-auto"
            data-testid="hero-logo"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="text-center px-6 mt-8"
        >
          <h1
            className="font-['Outfit'] text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 text-gradient-gold"
            data-testid="hero-title"
          >
            Conectando Marcas e Influenciadores
          </h1>
          <p className="font-['Plus_Jakarta_Sans'] text-base md:text-lg max-w-3xl mx-auto text-zinc-400 leading-relaxed" data-testid="hero-subtitle">
            Na Infinity Influencers, ampliamos a visibilidade da sua marca por meio de influenciadores qualificados. Conecte-se conosco e fortaleça sua presença no marketing digital.
          </p>
        </motion.div>

        <div className="scroll-indicator">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-6 h-10 border-2 border-gold rounded-full flex justify-center pt-2"
          >
            <motion.div className="w-1.5 h-2 bg-gold rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="sobre" className="py-24 md:py-32 relative" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-['Outfit'] text-3xl md:text-5xl font-semibold tracking-tight mb-6 text-gold" data-testid="about-title">
                Sobre Nós
              </h2>
              <p className="font-['Plus_Jakarta_Sans'] text-base md:text-lg text-zinc-400 leading-relaxed" data-testid="about-text">
                A Infinity Influencers é uma agência de marketing focada em conectar marcas a influenciadores digitais qualificados. Atuamos criando parcerias estratégicas que ampliam a visibilidade e o alcance das campanhas. Nossa equipe trabalha para gerar oportunidades que beneficiem ambos os lados, com foco em resultados consistentes.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_dc130d97-6b55-4e93-ba73-e94270a0cce0/artifacts/hw1t4bsv_83F90E6A-D660-4D81-B9F8-05EB1DFD85D0.png"
                alt="Partnership"
                className="w-full rounded-none shadow-2xl"
                data-testid="about-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <div className="section-divider max-w-7xl mx-auto" />

      {/* Why Work With Us Section */}
      <section id="porque" className="py-24 md:py-32" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-['Outfit'] text-3xl md:text-5xl font-semibold tracking-tight mb-16 text-center text-gold"
            data-testid="why-title"
          >
            Nossos Diferenciais
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: "Influenciadores Selecionados",
                description: "Trabalhamos com influenciadores qualificados e alinhados a cada proposta de campanha.",
                icon: <MessageSquare className="w-10 h-10 text-gold" />,
                testId: "why-card-1"
              },
              {
                title: "Estratégia de Campanha",
                description: "Desenvolvemos campanhas personalizadas, focadas em atender os objetivos de cada marca.",
                icon: <Send className="w-10 h-10 text-gold" />,
                testId: "why-card-2"
              },
              {
                title: "Parcerias de Alto Desempenho",
                description: "Buscamos parcerias que gerem impacto real e fortaleçam a presença da sua marca.",
                icon: <Phone className="w-10 h-10 text-gold" />,
                testId: "why-card-3"
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card p-8 bento-item group"
                data-testid={item.testId}
              >
                <div className="mb-6">{item.icon}</div>
                <h3 className="font-['Outfit'] text-2xl md:text-3xl font-medium mb-4 text-white group-hover:text-gold transition-colors">
                  {item.title}
                </h3>
                <p className="font-['Plus_Jakarta_Sans'] text-zinc-400">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-12 flex justify-center"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_dc130d97-6b55-4e93-ba73-e94270a0cce0/artifacts/82li8s1o_D7331EBE-AEE0-4C9E-B4CA-007563588018.png"
              alt="Social Proof"
              className="w-full max-w-4xl rounded-none shadow-2xl"
              data-testid="why-image"
            />
          </motion.div>
        </div>
      </section>

      <div className="section-divider max-w-7xl mx-auto" />

      {/* Contact Section */}
      <section id="contato" className="py-24 md:py-32" data-testid="contact-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-['Outfit'] text-3xl md:text-5xl font-semibold tracking-tight mb-12 text-center text-gold"
            data-testid="contact-title"
          >
            Contato
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-6 justify-center"
          >
            <a
              href="https://wa.me/5531973293878"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold-outline flex items-center justify-center gap-2"
              data-testid="contact-whatsapp-button"
            >
              <Phone className="w-5 h-5" />
              WhatsApp
            </a>
            <a
              href="https://t.me/Infinityinfluencers"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold-outline flex items-center justify-center gap-2"
              data-testid="contact-telegram-button"
            >
              <Send className="w-5 h-5" />
              Telegram
            </a>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-6">
          <img
            src="https://customer-assets.emergentagent.com/job_dc130d97-6b55-4e93-ba73-e94270a0cce0/artifacts/npzerimb_IMG_1769.png"
            alt="Infinity Influencers"
            className="h-10"
            data-testid="footer-logo"
          />
          <p className="text-zinc-600 text-sm" data-testid="footer-copyright">
            © 2026 Infinity Influencers. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;